import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetManagingDirectorsQuery,
  useGetMdSchedulesQuery,
} from "../../../store/appApi";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import { updateMdSchdule } from "../../../services/mdschdule";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const STATUS_OPTIONS = ["Upcoming", "Ongoing", "Completed"];

// Distinct color per status so the list scans quickly at a glance.
const STATUS_CHIP_CLASS = {
  Upcoming: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
  Ongoing: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
};

const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Display-only normalization — a lot of existing entries were typed in ALL
// CAPS, which reads as shouting in a list. Lowercases everything, then
// re-capitalizes the start of each sentence/numbered point, without ever
// touching the stored value (the edit input still shows the raw text).
const toSentenceCase = (str) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .replace(/(^\s*\w|[.!?]\s+\w)/g, (c) => c.toUpperCase());
};

const ListMdSchedule = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const currentUserRole = useSelector(selectUserRole);
  const isAdmin =
    currentUserRole === "super-admin" || currentUserRole === "sub-admin";

  const [filters, setFilters] = useState({ date: null, status: "", mdId: "" });
  const [savingId, setSavingId] = useState(null);
  const [rowEdits, setRowEdits] = useState({});

  const { data: mdData } = useGetManagingDirectorsQuery();
  const managingDirectors = useMemo(
    () =>
      Array.isArray(mdData?.managingDirectors) ? mdData.managingDirectors : [],
    [mdData],
  );

  // With exactly one Managing Director on record there's nothing to pick —
  // scope the list to them by default so schedules never look "mixed."
  useEffect(() => {
    if (managingDirectors.length === 1 && !filters.mdId) {
      setFilters((prev) => ({ ...prev, mdId: managingDirectors[0]._id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managingDirectors]);

  const { data, isLoading, isFetching } = useGetMdSchedulesQuery({
    date: filters.date ? toDateInputValue(filters.date) : "",
    status: filters.status,
    mdId: filters.mdId,
  });

  const schedules = useMemo(
    () => (Array.isArray(data?.schedule) ? data.schedule : []),
    [data],
  );

  const selectedMdName = filters.mdId
    ? managingDirectors.find((md) => md._id === filters.mdId)?.name
    : "";

  const getRowEdit = (row) =>
    rowEdits[row._id] || {
      status: row.status || "Upcoming",
      remarks: row.remarks || "",
    };

  const setRowEdit = (rowId, patch) =>
    setRowEdits((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), ...patch },
    }));

  const canEditRow = (row) =>
    isAdmin ||
    row.userId?.toString() === currentUser?.localId ||
    row.createdBy?.toString() === currentUser?.localId;

  const handleSaveRow = async (row) => {
    const edit = getRowEdit(row);
    try {
      setSavingId(row._id);
      await updateMdSchdule({
        id: row._id,
        status: edit.status,
        remarks: edit.remarks,
      });
      dispatch(appApi.util.invalidateTags(["MdSchedules"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message ||
          "Could not update this schedule entry.",
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="MD Schedule" motherMenu="MD Schedule" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                MD Schedule
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900">
                {selectedMdName ? `Schedule - ${selectedMdName}` : "Schedule Ledger"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Filter by date, and update status or remarks directly from
                the list.
              </p>
            </div>
            <button
              type="button"
              className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
              onClick={() => navigate("/md-schedule/create")}
            >
              + New Schedule
            </button>
          </div>
        </section>

        <section className={`${panelClass} p-5`}>
          <div className="grid gap-4 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Managing Director
              </label>
              <select
                className={inputClass}
                autoComplete="off"
                value={filters.mdId}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, mdId: event.target.value }))
                }
              >
                <option value="">All Managing Directors</option>
                {managingDirectors.map((md) => (
                  <option key={md._id} value={md._id}>
                    {md.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Date
              </label>
              <DatePicker
                selected={filters.date}
                onChange={(date) => setFilters((prev) => ({ ...prev, date }))}
                placeholderText="Filter by exact date"
                className={inputClass}
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                isClearable
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Status
              </label>
              <select
                className={inputClass}
                autoComplete="off"
                value={filters.status}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: event.target.value,
                  }))
                }
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                onClick={() =>
                  setFilters({ date: null, status: "", mdId: "" })
                }
              >
                Reset
              </button>
            </div>
          </div>
        </section>

        <section className={`${panelClass} p-5`}>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Schedule Entries
              </h2>
              <p className="text-sm text-gray-500">
                {schedules.length} entr{schedules.length === 1 ? "y" : "ies"}
              </p>
            </div>
          </div>

          {(isLoading || isFetching) && (
            <div className="rounded-xl border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
              Loading schedule...
            </div>
          )}

          {!isLoading && !isFetching && schedules.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
              No schedule entries found for the selected filters.
            </div>
          )}

          {!isLoading && !isFetching && schedules.length > 0 && (
            <div className="space-y-4">
              {schedules.map((row) => {
                const editable = canEditRow(row);
                const edit = getRowEdit(row);
                return (
                  <div
                    key={row._id}
                    className="rounded-2xl border border-gray-200 bg-white p-4 transition hover:border-gray-300 hover:shadow-sm sm:p-5"
                  >
                    {/* Title row — name + at-a-glance metadata */}
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="capitalize text-base font-semibold text-gray-900">
                          {toSentenceCase(row.scheduleName)}
                        </h3>
                        {row.description && (
                          <p className="capitalize mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-500">
                            {toSentenceCase(row.description)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                          {formatDateLabel(row.date)}
                        </span>
                        {!filters.mdId && row.user?.name && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                            {row.user.name}
                          </span>
                        )}
                        {!editable && (
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CHIP_CLASS[row.status] || ""}`}
                          >
                            {row.status || "Upcoming"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Edit row — status, remarks, save */}
                    {editable && (
                      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-4">
                        <div className="w-full sm:w-44">
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">
                            Status
                          </label>
                          <select
                            className={`${inputClass} ${STATUS_CHIP_CLASS[edit.status] || ""}`}
                            autoComplete="off"
                            value={edit.status}
                            onChange={(event) =>
                              setRowEdit(row._id, {
                                status: event.target.value,
                              })
                            }
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="min-w-[200px] flex-1">
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">
                            Remarks
                          </label>
                          <input
                            className={inputClass}
                            autoComplete="off"
                            value={edit.remarks}
                            onChange={(event) =>
                              setRowEdit(row._id, {
                                remarks: event.target.value,
                              })
                            }
                            placeholder="Add a remark"
                          />
                        </div>
                        <button
                          type="button"
                          className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => handleSaveRow(row)}
                          disabled={savingId === row._id}
                        >
                          {savingId === row._id ? "Saving..." : "Save"}
                        </button>
                      </div>
                    )}
                    {!editable && row.remarks && (
                      <div className="mt-3 border-t border-gray-100 pt-3 text-sm text-gray-600">
                        <span className="font-semibold text-gray-500">
                          Remarks:{" "}
                        </span>
                        {row.remarks}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default ListMdSchedule;
